import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AppRow } from '../../../../components/tanstack-table/features';
import { useResponsiveColumnSizing } from '../../../../components/tanstack-table/use-column-sizing';
import { useRouteRowSelection } from '../../../../components/tanstack-table/use-route-row-selection';
import { useTableRef } from '../../../../components/tanstack-table/use-table-ref';
import { isPivotEligible, type Product } from '../../../../data/product';
import { hasSwingDeals } from '../../../../data/states';
import { formatDateISO } from '../../../../helpers/format-date';
import { useKeyboardShortcut } from '../../../../helpers/use-keyboard-shortcut';
import { useEngagement } from '../../../../hooks/use-engagement';
import { useScrollPreservation } from '../../../../hooks/use-scroll-preservation';
import { useSearchFocus } from '../../../../hooks/use-search-focus';
import { useVendorIntegrations } from '../../../../hooks/vendors';
import { useReorder } from '../components/reorder-column';
import { useReviewOrder } from '../components/review-order';
import type { Family } from '../data/family';
import { itemHasCount, type ProductOrderItem } from '../data/product-order-item';
import type { EditOrderPopulatedProperties, OrderItemsViewModel } from '../types';
import { matchesIsBuyup, vendorHasVolumePricing } from '../utils';
import { usePivotGlowStore } from './pivot-glow-store';
import { BUYUP_COLUMNS_KEY, ORDER_COLUMNS_KEY, useColumns } from './use-columns';
import { useFilteredOrder } from './use-filtered-order';
import { useScrollToProduct } from './use-scroll-to-product';
import { useSortedOrder } from './use-sorted-order';

export type EditOrderViewModel = ReturnType<typeof useEditOrderView>;

/**
 * The one shared data hook behind the order-edit screen. Both <OrderView> and
 * <BuyupView> render from this view model; it lives in EditOrderPopulated so
 * cross-mode state (search query, filters, sort mode, scroll offsets, unsaved
 * edits) survives toggling between the two views.
 *
 * `useItems` is the v1-or-v2 data hook, chosen once by the shell's single
 * `isOrderEditV2Enabled` check (index.tsx) and passed in here rather than
 * called directly — that's what keeps this the only place that reads it.
 * Calling it as a plain function argument, rather than `flag ? useA() :
 * useB()`, is legal under the rules of hooks only because its identity is
 * constant for the component's lifetime: a store's flag does not flip
 * mid-session, so this is exactly as stable as calling one fixed hook always
 * would be.
 */
export function useEditOrderView(
  properties: EditOrderPopulatedProperties,
  useItems: (properties: EditOrderPopulatedProperties) => OrderItemsViewModel
) {
  const { store, vendor } = properties;

  const [isBuyup, setIsBuyup] = useState(false);
  const mode = isBuyup ? 'buyup' : 'order';
  const prePivotSearchRef = useRef<string | null>(null);
  const [displayInCases, setDisplayInCases] = useState(false);
  const searchReference = useSearchFocus();

  const { pivotTo: pivotGlowTo, reset: resetPivotGlow, glowingId } = usePivotGlowStore();

  useEffect(() => {
    return () => {
      resetPivotGlow();
    };
  }, [resetPivotGlow]);

  const [addModalFamily, setAddModalFamily] = useState<Family | null>(null);

  const orderState = useItems(properties);
  const { order, orderItems, setOrderItem, options } = orderState;

  const triggerBuyupEngagement = useEngagement('buyup_engaged', store.id);

  const wrappedSetOrderItem = useCallback(
    (item: ProductOrderItem) => {
      setOrderItem(item);
      if (isBuyup) {
        triggerBuyupEngagement({
          order_id: order.id,
          vendor_id: vendor.vendor_id,
          vendor_normalized_name: vendor.normalized_name,
        });
      }
    },
    [
      isBuyup,
      setOrderItem,
      triggerBuyupEngagement,
      order.id,
      vendor.vendor_id,
      vendor.normalized_name,
    ]
  );

  const reorderColumn = useReorder(options);

  const integrations = useVendorIntegrations();
  const vendorHasVolume = vendorHasVolumePricing(vendor, integrations);
  const isVolumeDeals = isBuyup && vendorHasVolume;
  const isSwing = hasSwingDeals(store);

  const {
    query,
    setQuery,
    filteredOrderItems,
    filterContext,
    orderProducts,
    showFilter,
    setShowFilter,
    activeFilterCount,
    clearFilters,
  } = useFilteredOrder(
    orderItems,
    isBuyup,
    // The family view renders disabled / no-recent-sales rows with badges, so
    // keep them in the working set instead of filtering them upstream.
    isVolumeDeals
  );
  const { sorting, sortPopoverSorting, handleSortPopoverChange } = useSortedOrder(
    reorderColumn.id,
    isBuyup
  );

  const hasBuyupData = useMemo(() => {
    return orderItems.some((orderItem) => matchesIsBuyup(orderItem.product, true));
  }, [orderItems]);

  const pivotToBuyup = useCallback(
    (product: Product) => {
      if (prePivotSearchRef.current === null) {
        prePivotSearchRef.current = query;
      }

      setQuery(product.brand);
      pivotGlowTo(product.id);
      setIsBuyup(true);

      requestAnimationFrame(() => {
        const input = searchReference.current;
        if (input) {
          input.focus();
          input.select();
        }
      });
    },
    [query, searchReference, setQuery, pivotGlowTo]
  );

  const pivotBrandWithinBuyup = useCallback(
    (product: Product) => {
      setQuery(product.brand);
      pivotGlowTo(product.id);

      requestAnimationFrame(() => {
        const input = searchReference.current;
        if (input) {
          input.focus();
          input.select();
        }
      });
    },
    [searchReference, setQuery, pivotGlowTo]
  );

  const setBuyupView = useCallback(
    (next: boolean) => {
      setIsBuyup((current) => {
        if (current && !next && prePivotSearchRef.current !== null) {
          setQuery(prePivotSearchRef.current);
          prePivotSearchRef.current = null;
        }

        return next;
      });
    },
    [setQuery]
  );

  useKeyboardShortcut('meta+b', () => {
    if (!hasBuyupData) {
      return;
    }

    setBuyupView(!isBuyup);
  });

  const handleSearchChange = useCallback(
    (value: string) => {
      setQuery(value);
      prePivotSearchRef.current = null;
    },
    [setQuery]
  );

  const showsSavingsIndicators = useMemo(() => {
    if (vendorHasVolume) {
      return orderItems.some((orderItem) => {
        return (
          (orderItem.product.pricing.previous_cost !== undefined &&
            orderItem.product.pricing.previous_volume_discounts &&
            orderItem.product.pricing.previous_volume_discounts.length > 0) ||
          (orderItem.product.pricing.current_cost !== undefined &&
            orderItem.product.pricing.volume_discounts &&
            orderItem.product.pricing.volume_discounts.length > 0) ||
          (orderItem.product.pricing.future_cost !== undefined &&
            orderItem.product.pricing.future_volume_discounts &&
            orderItem.product.pricing.future_volume_discounts.length > 0)
        );
      });
    }

    if (isSwing) {
      return orderItems.some((orderItem) => {
        return (
          orderItem.product.pricing.previous_cost !== undefined ||
          orderItem.product.pricing.current_cost !== undefined ||
          orderItem.product.pricing.future_cost !== undefined
        );
      });
    }

    return false;
  }, [orderItems, isSwing, vendorHasVolume]);

  const handleCasesSoldClick = useCallback(
    (product: Product, casesToOrder: number) => {
      if (!isBuyup) {
        return;
      }

      if (casesToOrder <= 0) {
        return;
      }

      const orderItem = orderItems.find((item) => item.product.id === product.id);
      if (orderItem) {
        wrappedSetOrderItem({
          ...orderItem,
          cases: casesToOrder,
          units: 0,
        });
      }
    },
    [isBuyup, orderItems, wrappedSetOrderItem]
  );

  // useColumns returns all columns (for TanStack sorting) + visibility state + visible columns (for layout)
  const { columns, visibleColumns, columnVisibility, columnOrder } = useColumns({
    store,
    vendorHasVolume,
    setOrderItem: wrappedSetOrderItem,
    reorderColumn,
    isBuyup,
    showsSavingsIndicators,
    displayInCases,
    onCasesSoldClick: handleCasesSoldClick,
    onPivotToBuyup: pivotToBuyup,
    onBrandPivot: pivotBrandWithinBuyup,
  });

  const columnsTableKey = isBuyup ? BUYUP_COLUMNS_KEY : ORDER_COLUMNS_KEY;

  const [showSubmitOrder, setShowSubmitOrder] = useState(false);
  const review = useReviewOrder({
    store,
    vendor,
    order,
    orderItems,
    methods: options.send_methods,
  });

  const toggleShowSubmitOrder = useCallback(() => {
    setShowSubmitOrder((show) => !show);
  }, []);

  const tableScrollRef = useRef<HTMLDivElement>(null);
  const scrollOffsetsStore = useRef<Record<string, number>>({});
  const { onScroll: onTableScroll } = useScrollPreservation(tableScrollRef, query !== '', {
    viewKey: isBuyup ? 'buyup' : 'regular',
    store: scrollOffsetsStore,
  });

  // Subscribes to the pivot-glow store and scrolls the flat-table row into
  // view when present. Family-view has its own virtualizer-aware variant; the
  // querySelector here returns null when family-view is rendered, so it's a
  // no-op without further gating.
  useScrollToProduct({ scrollRef: tableScrollRef });

  // Row selection for highlighting - separate selections for buyup vs regular views
  const {
    rowSelection,
    onRowSelectionChange,
    onRowClick: toggleRowSelection,
  } = useRouteRowSelection(isBuyup ? 'buyup' : 'regular');

  const handleRowClick = useCallback(
    (row: AppRow<ProductOrderItem>, event: React.MouseEvent) => {
      if (!isBuyup && (event.metaKey || event.ctrlKey)) {
        const product = row.original.product;
        if (isPivotEligible(product, vendorHasVolume)) {
          pivotToBuyup(product);

          return;
        }
      }

      toggleRowSelection(row);
    },
    [isBuyup, pivotToBuyup, toggleRowSelection, vendorHasVolume]
  );

  const { columnSizing } = useResponsiveColumnSizing(visibleColumns);

  // visibleColumns is in column-definition order, but the table header renders
  // in columnOrder (the user's drag order). The summary cells must follow the
  // same order or they drift out of alignment with the headers as soon as a
  // column is reordered.
  const orderedVisibleColumns = useMemo(() => {
    const byId = new Map(visibleColumns.map((col) => [col.id, col]));

    return columnOrder.flatMap((id) => {
      const col = byId.get(id);

      return col ? [col] : [];
    });
  }, [visibleColumns, columnOrder]);

  // Table ref for CSV export
  const [table, onTableReady] = useTableRef<ProductOrderItem>();

  // Non-ordering rows are still the primary scanning surface (the user is
  // reading them to decide whether to stock up), so don't dim them — just
  // shift the text to a slightly softer gray so committed (ordering) rows
  // read at full contrast by comparison.
  const getRowClassName = useCallback(
    (item: ProductOrderItem) => {
      const isGlowing = item.product.id === glowingId;
      const glowClass = isGlowing ? 'animate-pivot-glow' : '';

      return [itemHasCount(item) ? '' : 'text-gray-700!', glowClass].filter(Boolean).join(' ');
    },
    [glowingId]
  );

  // Ordering rows get a wider stripe (full lane) so they pop out of the
  // flow; non-ordering rows get a thinner stripe within the same reserved
  // lane so row width stays constant. The ordering stripe uses the same brand
  // emerald as the row-selection rail so the two greens read as one system.
  const getRowStripe = useCallback((item: ProductOrderItem) => {
    return itemHasCount(item)
      ? { color: 'var(--color-primary-700)', width: 6 }
      : { color: 'var(--color-gray-400)', width: 3 };
  }, []);

  const csvFilename = `${vendor.name}-${mode}-${formatDateISO(new Date())}.csv`;

  return {
    store,
    vendor,
    ...orderState,
    setOrderItem: wrappedSetOrderItem,

    isBuyup,
    mode,
    setBuyupView,
    hasBuyupData,
    isVolumeDeals,
    isSwing,
    displayInCases,
    setDisplayInCases,

    query,
    handleSearchChange,
    searchReference,
    filteredOrderItems,
    filterContext,
    orderProducts,
    showFilter,
    setShowFilter,
    activeFilterCount,
    clearFilters,

    sorting,
    sortPopoverSorting,
    handleSortPopoverChange,
    columns,
    columnVisibility,
    columnOrder,
    columnsTableKey,
    columnSizing,
    orderedVisibleColumns,

    table,
    onTableReady,
    tableScrollRef,
    scrollOffsetsStore,
    onTableScroll,
    rowSelection,
    onRowSelectionChange,
    handleRowClick,
    getRowClassName,
    getRowStripe,
    csvFilename,

    addModalFamily,
    setAddModalFamily,
    showSubmitOrder,
    toggleShowSubmitOrder,
    review,
  };
}
